import { createStore, select, setProp, setProps, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import {
  getAllEntities,
  selectAllEntities,
  selectEntitiesCount,
  setEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { HttpErrorResponse } from '@angular/common/http';

import { INotification } from './notification.model';

export interface NotificationsProps {
  currentPage: number;
  totalPages: number | null;
  totalItems: number | null;
  nextPage: number | null;
  numberOfUnread: number;
  mode: 'unread' | 'all';
  wasDialogOpened: boolean;
  notificationStreamData: {
    authToken: string | null;
    topics: string[] | null;
  };
  loading: boolean;
  error: HttpErrorResponse | null;
}

const initialProps: NotificationsProps = {
  currentPage: 1,
  totalPages: null,
  totalItems: null,
  nextPage: null,
  numberOfUnread: 0,
  mode: 'unread',
  wasDialogOpened: false,
  notificationStreamData: {
    authToken: null,
    topics: null,
  },
  loading: false,
  error: null,
};

const STORE_NAME = 'sandoz-v2-notifications';

@Injectable({ providedIn: 'root' })
export class NotificationsRepository {
  private readonly store;

  public readonly notifications$: Observable<INotification[]>;
  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<HttpErrorResponse | null>;
  public readonly totalItems$: Observable<number | null>;
  public readonly currentPage$: Observable<number>;
  public readonly nextPage$: Observable<number | null>;
  public readonly numberOfUnreadNotifications$: Observable<number>;
  public readonly mode$: Observable<'unread' | 'all'>;
  public readonly authToken$: Observable<string | null>;
  public readonly topics$: Observable<string[] | null>;
  public readonly count$: Observable<number>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<NotificationsProps>(initialProps),
      withEntities<INotification>(),
    );

    this.notifications$ = this.store.pipe(
      selectAllEntities(),
      // Add filter to remove any undefined values
      map(notifications => notifications.filter(n => !!n)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.loading$ = this.store.pipe(
      select(state => state.loading),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.error$ = this.store.pipe(
      select(state => state.error),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.totalItems$ = this.store.pipe(
      select(state => state.totalItems),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.currentPage$ = this.store.pipe(
      select(state => state.currentPage),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.nextPage$ = this.store.pipe(
      select(state => state.nextPage),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.numberOfUnreadNotifications$ = this.store.pipe(
      select(state => state.numberOfUnread),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.mode$ = this.store.pipe(
      select(state => state.mode),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.authToken$ = this.store.pipe(
      select(state => state.notificationStreamData.authToken),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.topics$ = this.store.pipe(
      select(state => state.notificationStreamData.topics),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.count$ = this.store.pipe(
      selectEntitiesCount(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  resetStore(resetAll = false): void {
    if (resetAll) {
      this.store.reset();
    } else {
      const { notificationStreamData } = this.store.getValue();
      this.store.reset();
      this.store.update(state => ({
        ...state,
        notificationStreamData,
      }));
    }
  }

  getState(): NotificationsProps & {
    entities: Record<string | number, INotification>;
  } {
    return this.store.getValue();
  }

  getStoreProp<K extends keyof NotificationsProps>(
    key: K,
  ): NotificationsProps[K] {
    return this.store.getValue()[key];
  }

  getIds(): number[] {
    return this.store.query(getAllEntities()).map(entity => entity.id);
  }

  // #region Entities
  setNotifications(notifications: INotification[]): void {
    this.store.update(setEntities(notifications));
  }

  upsertNotifications(notifications: INotification[]): void {
    this.store.update(upsertEntities(notifications));
  }

  removeNotifications(ids: number[]): void {
    this.store.update(state => {
      const newEntities = { ...state.entities };
      ids.forEach(id => delete newEntities[id]);

      return { ...state, entities: newEntities };
    });
  }
  // #endregion

  // #region Props
  setLoading(loading: boolean): void {
    this.store.update(setProp('loading', loading));
  }

  setError(error: HttpErrorResponse | null): void {
    this.store.update(setProp('error', error));
  }

  setPage(currentPage: number): void {
    if (currentPage > this.getState().totalPages!) return;

    this.store.update(setProp('currentPage', currentPage));
  }

  setMode(mode: 'unread' | 'all'): void {
    this.setNotifications([]);

    this.store.update(setProps({ currentPage: 1, mode: mode }));
  }

  setWasDialogOpened(wasDialogOpened: boolean): void {
    this.store.update(setProp('wasDialogOpened', wasDialogOpened));
  }

  setUnreadNumber(numberOfUnread: number): void {
    this.store.update(setProp('numberOfUnread', numberOfUnread));
  }

  updateState(data: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    nextPage: number;
    numberOfUnread?: number;
  }): void {
    this.store.update(state => ({
      ...state,
      currentPage: data.currentPage,
      totalPages: data.totalPages,
      totalItems: data.totalItems,
      nextPage: data.nextPage,
      numberOfUnread: data.numberOfUnread ?? state.numberOfUnread,
    }));
  }

  updateNotificationsReadStatus(ids: number[], read: boolean): void {
    this.store.update(state => {
      const newEntities = { ...state.entities };
      ids.forEach(id => {
        if (newEntities[id]) {
          newEntities[id] = { ...newEntities[id], read };
        }
      });

      return { ...state, entities: newEntities };
    });
  }
  // #endregion
}
